import React, { useEffect, useState } from "react";
import { Search, X, XCircle } from "lucide-react";

const SearchBar = ({
    value,
    onChange,
    placeholder = "Search...",
    filters = null,
    actions = null,
    className = "",
    showClearAll = false,
    onClearAll,
    secondRowRight = null,
}) => {
    // local draft value — typing does NOT call onChange (no API call on every keystroke)
    const [draft, setDraft] = useState(value || "");

    // keep draft in sync if parent resets/changes value from outside
    useEffect(() => {
        setDraft(value || "");
    }, [value]);

    const triggerSearch = (val) => {
        onChange(val);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            triggerSearch(draft);
        }
    };

    const handleClear = () => {
        setDraft("");
        triggerSearch("");
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        setDraft(val);
        // value cleared out (e.g. backspace) -> fire search right away
        if (val === "") {
            triggerSearch("");
        }
    };

    return (
        <div className={`flex flex-col gap-3 p-4${className}`}>
            {/* Row 1 — original: search input + filters + actions */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                    <div className="flex w-full items-center overflow-hidden rounded-lg border border-gray-300 transition-all duration-200 focus-within:border-brand-500 focus-within:shadow-md focus-within:ring-2 focus-within:ring-app-accent/20 dark:border-gray-700 dark:focus-within:border-brand-500 dark:focus-within:ring-brand-500/20 sm:w-72">
                        <div className="relative flex-1">
                            <input
                                type="text"
                                value={draft}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                placeholder={placeholder}
                                className="w-full bg-transparent py-2 pl-3 pr-8 text-sm text-gray-700 border-none outline-none dark:text-gray-300"
                            />
                            {draft && (
                                <button
                                    type="button"
                                    onClick={handleClear}
                                    title="Clear"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>

                        <button
                            type="button"
                            onClick={() => triggerSearch(draft)}
                            title="Search"
                            className="flex items-center justify-center border-l border-gray-300 px-3 py-2 text-gray-400 hover:text-brand-500 dark:border-gray-700"
                        >
                            <Search size={16} />
                        </button>
                    </div>

                    {filters}
                </div>

                {actions && <div className="flex items-center gap-3">{actions}</div>}
            </div>

            {/* Row 2 — justify-between: Clear All (left) and Add button (right) */}
            {(showClearAll || secondRowRight) && (
                <div className="flex items-center justify-between border-t border-gray-100 pt-3 dark:border-gray-800">
                    <div>
                        {showClearAll && (
                            <button
                                type="button"
                                onClick={onClearAll}
                                title="Clear all filters"
                                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 hover:border-red-400 hover:text-red-500 dark:border-gray-700 dark:text-gray-300 dark:hover:border-red-400 dark:hover:text-red-400"
                            >
                                <XCircle size={14} />
                                Clear All
                            </button>
                        )}
                    </div>

                    <div>{secondRowRight}</div>
                </div>
            )}
        </div>
    );
};

export default SearchBar;